import { useMutation } from '@apollo/client';
import { Alert, AlertTitle, Box, Button, Heading, Paragraph, Stack, Text, useNotifications } from '@sixfold/common-ui';
import copy from 'copy-to-clipboard';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useIsAdmin } from '../../lib/authorization';
import { CreateOvdTokenMutation, CreateOvdTokenMutationVariables } from '../../lib/graphql';
import { Routes } from '../../routes';
import { OvdTokenForm, OvdTokenFormValues, parseCommaSeparated } from '../components/ovd_token_form';
import { createOvdTokenMutation, ovdTokensQuery } from '../graphql';

export const OvdTokenCreateContainer = () => {
  const navigate = useNavigate();
  const notify = useNotifications();
  const isAdmin = useIsAdmin();
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [createToken, { loading }] = useMutation<CreateOvdTokenMutation, CreateOvdTokenMutationVariables>(
    createOvdTokenMutation,
  );

  const handleSubmit = async (values: OvdTokenFormValues) => {
    const shipperIds = parseCommaSeparated(values.shipperIds);
    const carrierIds = parseCommaSeparated(values.carrierIds);
    const deliveryCustomFields = parseCommaSeparated(values.deliveryCustomFields);
    const tourCustomFields = parseCommaSeparated(values.tourCustomFields);

    let result;
    try {
      result = await createToken({
        variables: {
          input: {
            label: values.label,
            platformIds: parseCommaSeparated(values.platformIds),
            ...(shipperIds.length > 0 ? { shipperIds } : {}),
            ...(carrierIds.length > 0 ? { carrierIds } : {}),
            ...(deliveryCustomFields.length > 0 ? { deliveryCustomFields } : {}),
            ...(tourCustomFields.length > 0 ? { tourCustomFields } : {}),
            enableStatus: values.enableStatus,
            enableApiEta: values.enableApiEta,
            enableCustomFields: values.enableCustomFields,
            enableCancellationReason: values.enableCancellationReason,
            enableLocation: values.enableLocation,
          },
        },
        refetchQueries: [{ query: ovdTokensQuery }],
      });
    } catch (e) {
      console.error(e);
      notify.error({
        title: 'Failed to create OVD token',
        message: e instanceof Error ? e.message : undefined,
      });
      return;
    }

    const created = result.data?.createOvdToken;

    if (!created) {
      notify.warning({
        title: 'Could not confirm token creation',
        message:
          'The server did not return token details. Check the token list before creating another, to avoid duplicates.',
      });
      void navigate(Routes.OvdTokens.generatePath({}));
      return;
    }

    const rawToken = created.token;
    if (rawToken) {
      setCreatedToken(rawToken);
      notify.success({ title: 'OVD token created' });
      return;
    }

    notify.success({
      title: 'OVD token created',
      message:
        'The raw secret was not included in the response. You can reveal it later from the OVD tokens list using the reveal button.',
    });
    void navigate(Routes.OvdTokenDetail.generatePath({ token_id: created.tokenId }));
  };

  const handleCopy = async (token: string) => {
    const copied = await copy(token);
    if (copied) {
      notify.success({ title: 'Token copied to clipboard' });
    }
  };

  if (!isAdmin) {
    return (
      <Stack direction="column" gap="medium">
        <Heading size="medium">Create OVD Token</Heading>
        <Text>You do not have permission to create OVD tokens.</Text>
        <Button kind="subtle" onClick={() => navigate(Routes.OvdTokens.generatePath({}))}>
          Back to List
        </Button>
      </Stack>
    );
  }

  if (createdToken !== null) {
    return (
      <Stack direction="column" gap="medium">
        <Heading size="medium">Token Created Successfully</Heading>
        <Alert kind="warning">
          <AlertTitle>Copy your token now</AlertTitle>
          <Paragraph>
            Copy the token now and store it securely. You can reveal it again later from the token list, but it requires
            completing a captcha verification each time.
          </Paragraph>
        </Alert>
        <Box
          style={{
            padding: '12px',
            background: 'var(--color-surface-raised)',
            borderRadius: '4px',
            fontFamily: 'monospace',
            wordBreak: 'break-all',
          }}>
          {createdToken}
        </Box>
        <Stack direction="row" gap="small">
          <Button kind="primary" onClick={() => void handleCopy(createdToken)}>
            Copy Token
          </Button>
          <Button kind="subtle" onClick={() => navigate(Routes.OvdTokens.generatePath({}))}>
            Back to List
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap="medium">
      <Heading size="medium">Create OVD Token</Heading>
      <OvdTokenForm
        onSubmit={handleSubmit}
        onCancel={() => navigate(Routes.OvdTokens.generatePath({}))}
        isSubmitting={loading}
        submitLabel="Create Token"
      />
    </Stack>
  );
};
